import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "../api";
import { Card, Btn, Badge, Spinner, Empty } from "../ui";
import { ArrowLeft, ListPlus, Send, Pause, Play, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = { draft: "slate", queued: "blue", sending: "amber", paused: "amber", completed: "green" };
const Q_COLOR = { pending: "slate", sending: "amber", sent: "green", failed: "red" };

const StatBox = ({ label, value }) => (
  <Card className="p-4 text-center">
    <div className="text-2xl font-semibold text-slate-900">{value}</div>
    <div className="text-xs text-slate-400 mt-0.5">{label}</div>
  </Card>
);

export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: camp }, { data: q }] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/queue`, { params: { limit: 100 } }),
      ]);
      setC(camp); setQueue(q.items);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (action, fn) => {
    setBusy(action);
    try { await fn(); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(""); }
  };

  const enqueue = () => act("enqueue", async () => {
    const { data } = await api.post(`/campaigns/${id}/enqueue`);
    toast.success(`${data.enqueued} contacte adaugate in coada`);
  });
  const send = () => act("send", async () => {
    const { data } = await api.post(`/campaigns/${id}/send`);
    toast.success(`Trimise ${data.sent}, esuate ${data.failed}`);
  });
  const pause = () => act("pause", async () => { await api.post(`/campaigns/${id}/pause`); toast.success("Pus pe pauza"); });
  const resume = () => act("resume", async () => { await api.post(`/campaigns/${id}/resume`); toast.success("Reluat"); });

  if (loading) return <Spinner />;
  if (!c) return null;
  const s = c.stats || {};

  return (
    <div>
      <button onClick={() => nav("/campaigns")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-campaigns">
        <ArrowLeft className="w-4 h-4" /> Inapoi la campanii
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{c.name}</h1>
            <Badge color={STATUS_COLOR[c.status]}>{c.status}</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">{c.from_email} · provider {c.provider}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Btn variant="outline" onClick={enqueue} disabled={!!busy} data-testid="enqueue-button"><ListPlus className="w-4 h-4" /> {busy === "enqueue" ? "…" : "Pune in coada"}</Btn>
          <Btn onClick={send} disabled={!!busy} data-testid="send-button"><Send className="w-4 h-4" /> {busy === "send" ? "…" : "Trimite"}</Btn>
          {c.status === "paused"
            ? <Btn variant="outline" onClick={resume} disabled={!!busy} data-testid="resume-button"><Play className="w-4 h-4" /> Reia</Btn>
            : <Btn variant="outline" onClick={pause} disabled={!!busy} data-testid="pause-button"><Pause className="w-4 h-4" /> Pauza</Btn>}
          <Btn variant="ghost" onClick={load} data-testid="refresh-button"><RefreshCw className="w-4 h-4" /></Btn>
        </div>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
        <StatBox label="Total" value={s.total || 0} />
        <StatBox label="Trimise" value={s.sent || 0} />
        <StatBox label="In coada" value={s.pending || 0} />
        <StatBox label="Esuate" value={s.failed || 0} />
        <StatBox label="Deschise" value={s.opened || 0} />
        <StatBox label="Click-uri" value={s.clicked || 0} />
      </div>

      <Card>
        <div className="px-5 py-3 border-b border-slate-100 font-medium text-slate-900">Coada de mesaje</div>
        {queue.length === 0 ? (
          <Empty icon={Mail} title="Coada este goala" hint="Apasa Pune in coada pentru a adauga contactele." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="queue-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Deschideri</th>
                  <th className="px-5 py-3 font-medium">Click-uri</th>
                  <th className="px-5 py-3 font-medium">Message ID</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((m) => (
                  <tr key={m.id} className="border-b border-slate-50">
                    <td className="px-5 py-3 font-medium text-slate-900">{m.email}</td>
                    <td className="px-5 py-3"><Badge color={Q_COLOR[m.status]}>{m.status}</Badge></td>
                    <td className="px-5 py-3 text-slate-600">{m.opens}</td>
                    <td className="px-5 py-3 text-slate-600">{m.clicks}</td>
                    <td className="px-5 py-3 text-slate-400 font-mono text-xs">{m.message_id || m.error || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
